import axios from 'axios';
import { apiMainUrl } from '../apiConfig';

const API_BASE_URL = apiMainUrl + 'Tickets'; // Adjust the URL based on your actual API's URL

// Create a new support ticket
const createTicket = async (ticket, files, token) => {
    try {
        const formData = new FormData();
        formData.append('requesterEmail', ticket.requesterEmail);
        formData.append('subject', ticket.subject);
        formData.append('typeOfHelp', ticket.typeOfHelp);
        formData.append('urgencyLevel', ticket.urgencyLevel);
        formData.append('contactChannel', ticket.contactChannel);
        formData.append('problemDescription', ticket.problemDescription);

        // Append each file to the form data using the correct format for arrays in FormData
        files.forEach((file) => {
            formData.append('Files', file); // Notice we are not using an index here
        });

        const response = await axios.post(`${API_BASE_URL}`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    } catch (error) {
        console.error('Failed to create ticket:', error);
        return error.response;
    }
};

// Fetch recent tickets for the current user
const getRecentTickets = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/recent`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch recent tickets:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

// Fetch all tickets (admin view)
const getAllTickets = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/all`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch all tickets:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

// Fetch all open tickets (admin view)
const getOpenTickets = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/open`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch open tickets:', error);
        throw error; // Re-throw the error for handling by the caller
    }
};

// Get details of a specific ticket by ID
const getTicketById = async (id, token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/${id}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error(`Failed to fetch ticket with ID ${id}:`, error);
        throw error; // Re-throw the error for handling by the caller
    }
};

// Update an existing ticket
const updateTicket = async (id, ticket, token) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/${id}`, ticket, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error(`Failed to update ticket with ID ${id}:`, error);
        throw error; // Re-throw the error for handling by the caller
    }
};

const getMonthlyTicketStats = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/monthly-stats`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch monthly ticket stats:', error);
        throw error;
    }
};

const getMonthlyRequestDetails = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/monthly-details`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch monthly request details:', error);
        throw error;
    }
};

const getTicketComments = async (ticketId, token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/${ticketId}/comments`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch ticket comments:', error);
        throw error;
    }
};

const updateTicketStatus = async (ticketId, newStatus, comment, token) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/${ticketId}/status`, {
            newStatus,
            comment
        }, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to update ticket status:', error);
        throw error;
    }
};

const postComment = async (ticketId, comment, token) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/${ticketId}/comment`, comment , {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to post comment:', error);
        throw error;
    }
};

const getTicketStatusHistory = async (ticketId, token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/${ticketId}/status-changes`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch ticket status changes:', error);
        throw error;
    }
};

const addFilesToTicket = async (ticketId, files, token) => {
    try {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file); // Append each file
        });

        const response = await axios.post(`${API_BASE_URL}/${ticketId}/add-files`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to upload additional files:', error);
        throw error;
    }
};

const updateTicketData = async (ticketFormData, token) => {
    try {

        const response = await axios.post(`${API_BASE_URL}/update`, ticketFormData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    } catch (error) {
        console.error('Failed to upload additional files:', error);
        return error.response;
    }
};

export const getFileSasUri = async (fileName, token) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/files/sas-uri`, fileName, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data.sasUri;
    } catch (error) {
        console.error('Failed to retrieve SAS URI for file:', error);
        throw error;
    }
};

export const deleteFile = async (ticketId, fileUrl, token) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/${ticketId}/files`, {
            params: { fileUrl },
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response;
    } catch (error) {
        console.error('Failed to delete file:', error);
        throw error;
    }
};


export const TicketsDataService = {
    createTicket,
    getRecentTickets,
    getAllTickets,
    getTicketById,
    updateTicket,
    getOpenTickets,
    getMonthlyTicketStats,
    getMonthlyRequestDetails,
    getTicketComments,
    updateTicketStatus,
    postComment,
    getTicketStatusHistory,
    addFilesToTicket,
    getFileSasUri,
    deleteFile,
    updateTicketData
};
